import React, { useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@frontend/shadcn/components/ui/accordion';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Label } from '@frontend/shadcn/components/ui/label';
import {
  useArchiveProgramById,
  useDeleteProgramById,
  useGetCampaignByProjectId,
  useGetAllProjectsQuery,
  useGetCountsForProjectQuery,
  useClientFeatureEnabled
} from '@frontend/app/hooks';
import {
  BoxArchiveIcon,
  CircleCheckIcon,
  CircleExclamationIcon,
  TrashIcon,
  TriangleExclamationIcon,
} from '@revfluence/fresh-icons/regular/esm'; // Assuming these are the correct icons from AboutDrawer
import { ClientFeature } from '@frontend/app/constants/clientFeatures'; // Added
import { isNull } from 'lodash'; // Added
import { message, Modal, Switch } from '@revfluence/fresh'; // Added
import { useHistory } from 'react-router-dom'; // Added
import { TeamMemberSelect } from '@frontend/app/components/TeamMemberSelect/TeamMemberSelect'; // Added
import { useClientFeatures } from '@frontend/context/ClientFeatureContext'; // Added
import { GearIcon } from '@revfluence/fresh-icons/solid/esm';
import { SectionCardCollapsable } from '../../CampaignPage/components/SectionCardCollapsable';
import { ProjectsRouteRoot } from '../../constants'; // Added
import { TProject } from '../../types'; // Added
import { useCampaign } from '../../CampaignPage/CampaignContext'; // Added

interface AdditionalSettingsSectionProps {
  project: TProject;
}

export const AdditionalSettingsSection: React.FC<AdditionalSettingsSectionProps> = ({ project }) => {
  const history = useHistory();

  const {
    contentReviewEnabled,
    contentReviewers,
    gcrHideApprovalAction,
    setContentReviewEnabled,
    setContentReviewers,
    setGcrHideApprovalAction,
  } = useCampaign();

  const { [ClientFeature.GCR_HIDE_APPROVAL_ACTION]: isGCRHideApprovalActionEnabled } = useClientFeatures();

  const isArchiveProjectEnabled = useClientFeatureEnabled(ClientFeature.ARCHIVE_PROJECT);
  const isArchivedProject = useMemo(
    () => !isNull(project?.archivedDate) && !isNull(project?.archivedByUser),
    [project],
  );

  const { refetch: refetchProjectCampaign } = useGetCampaignByProjectId(project?.id, {
    skip: !project?.id,
  });
  const { refetch: refetchProjects } = useGetAllProjectsQuery();
  const { data: { counts = {
    all_in_progress: 0,
    new: 0,
    invited: 0,
  } } = {} } = useGetCountsForProjectQuery({
    variables: { projectId: project.id },
  });

  const totalCount = useMemo(() => {
    if (!counts) return 0;
    return counts.all_in_progress + counts.new + counts.invited;
  }, [counts]);

  const [archiveProgramById] = useArchiveProgramById({
    async onCompleted() {
      await refetchProjectCampaign();
      message.success({
        icon: <CircleCheckIcon />,
        content: 'Project Successfully Archived',
        duration: 2,
      });
      history.replace({
        pathname: `${ProjectsRouteRoot}/${project.id}/overview`,
      });
    },
    onError(error) {
      message.error({ content: error.message, duration: 2 });
    },
  });

  const { deleteProgramById } = useDeleteProgramById({
    onCompleted() {
      message.success({
        icon: <CircleCheckIcon />,
        content: `${project?.title} has been deleted.`,
        duration: 2,
      });
      refetchProjects();
      history.push('/home');
    },
    onError(error) {
      message.error({ content: error.message, duration: 2 });
    },
  });

  const archiveModalText = useMemo(
    () => (
      <>
        <p>
          Are you sure you want to archive this project? All project analytics will still be available but you will not be able to add or accept new members.
        </p>
        <p>You will not be able to unarchive at this time.</p>
      </>
    ),
    [],
  );

  const showArchiveModal = useCallback(() => {
    Modal.confirm({
      title: 'Archive Project',
      icon: <CircleExclamationIcon />,
      content: archiveModalText,
      onOk() {
        archiveProgramById({ variables: { id: project.id } });
      },
      autoFocusButton: null,
      okText: 'Archive',
    });
  }, [project?.id, archiveModalText, archiveProgramById]);

  const showDeleteProjectModal = useCallback(() => {
    Modal.confirm({
      title: `Permanently Delete ${project?.title}?`,
      content: (
        <>
          <p>Are you sure you want to permanently delete this project? This will have a number of consequences across Aspire:</p>
          <ul>
            <li>Your landing page for this project will be disabled. Any invites you have sent will no longer work.</li>
            <li>You will no longer be able to filter reports on content, posts, payments, or others by this project.</li>
            {totalCount > 0 && (
              <li>
                {totalCount} member{totalCount > 1 ? 's' : ''} in the workflow will have their workflows cancelled.
              </li>
            )}
          </ul>
        </>
      ),
      async onOk() {
        await deleteProgramById({ variables: { id: project.id } });
      },
      okType: 'danger',
      icon: <TriangleExclamationIcon />,
      cancelText: 'Cancel',
      okText: 'Yes, Delete',
    });
  }, [project?.title, project?.id, totalCount, deleteProgramById]);

  return (
    <SectionCardCollapsable
      icon={<GearIcon />}
      title="Additional Settings"
      subtitle="Manage additional campaign settings and options."
      defaultExpanded
    >
      <div className="pt-0">
        <Accordion type="multiple" className="w-full [&_h3]:mb-0" defaultValue={['group-content-review', 'archive-campaign', 'delete-campaign']}>
          <AccordionItem value="group-content-review" className="border-t border-border">
            <AccordionTrigger className="py-2 hover:no-underline text-md font-medium text-foreground">
              Group Content Review
            </AccordionTrigger>
            <AccordionContent
              className="pb-4 space-y-4"
              style={{
              overflow: 'visible'
            }}
            >
              <p className="text-sm text-grey-5">
                Toggling this setting will enable creator-submitted content to be reviewed by users before approval.
              </p>
              <div className="flex items-center gap-2">
                <Switch
                  id="group-review-toggle"
                  checked={contentReviewEnabled}
                  onChange={setContentReviewEnabled}
                  size="small"
                />
                <Label htmlFor="group-review-toggle" className="text-sm font-normal text-foreground">
                  Enable Group Content Review
                </Label>
              </div>

              {contentReviewEnabled && (
                <div className="space-y-2">
                  <Label className="text-xs block">
                    Additional Content Approvers
                    <span className="text-primaryMuted font-normal ml-1">(optional)</span>
                  </Label>
                  <TeamMemberSelect
                    value={contentReviewers}
                    onChange={setContentReviewers}
                    includeGuests
                    placeholder="Select team members"
                    className="w-full"
                  />
                </div>
              )}
              {contentReviewEnabled && isGCRHideApprovalActionEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="gcr-hide-approval-action"
                      checked={gcrHideApprovalAction}
                      onChange={setGcrHideApprovalAction}
                      size="small"
                    />
                    <Label htmlFor="gcr-hide-approval-action" className="text-sm font-normal text-foreground">Hide Approval Action</Label>
                  </div>
                  <p className="text-xs text-grey-5">
                    When Approval action is hidden, the Content Reviewers can only either Reject or Approve with Comments. In both these scenarios, you will be able to review their decision and comments and manage the communications to the creators.
                  </p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {isArchiveProjectEnabled && (
            <AccordionItem value="archive-campaign" className="border-t border-border">
              <AccordionTrigger className="py-2 hover:no-underline text-md font-medium text-foreground">
                Archive Campaign
              </AccordionTrigger>
              <AccordionContent className="pb-4 space-y-4">
                {isArchivedProject ? (
                  <p className="text-sm text-grey-5 mb-0">
                    This campaign was deactivated and will not count towards the active campaign cap for your plan. Members
                    can still continue to complete their tasks and their data will continue to be available in the campaign. You
                    will not be able to unarchive a campaign.
                    <a href="https://intercom.help/aspireiq_elevate/en/articles/6523259-how-to-archive-projects" className="font-medium underline ml-1 text-primary hover:text-primary" target="_blank" rel="noopener noreferrer">Learn more</a>
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-grey-5 mb-0">
                      This campaign will be deactivated and will not count towards the active campaign cap for your plan.
                      Members can still continue to complete their tasks and their data will continue to be available in the campaign.
                      You will not be able to unarchive a campaign. <a href="https://intercom.help/aspireiq_elevate/en/articles/6523259-how-to-archive-projects" className="font-medium underline ml-1 text-primary hover:text-primary" target="_blank" rel="noopener noreferrer">Learn more</a>
                    </p>
                    <Button variant="outline" onClick={showArchiveModal} className="gap-2">
                      <BoxArchiveIcon />
                      Archive Campaign
                    </Button>
                  </>
                )}
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="delete-campaign" className="border-t border-b-0 border-border">
            <AccordionTrigger className="py-2 hover:no-underline text-md font-medium text-foreground">
              Permanently Delete Campaign
            </AccordionTrigger>
            <AccordionContent className="pb-4 space-y-4">
              <p className="text-sm text-grey-5 mb-0">
                This will delete your campaign and its data completely. Use caution as this cannot be undone and member progress will be lost.
              </p>
              <Button variant="destructiveOutline" onClick={showDeleteProjectModal} className="gap-2">
                <TrashIcon />
                Delete Campaign
              </Button>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </SectionCardCollapsable>
  );
};
